"use client";
import React from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Home/ThemesInPlay/ThemesInPlay";

import { SectionHeader } from "@/src/components/SectionHeader";
import { ThemeCardV2 } from "@/src/componentsv1/themes/ThemeCardV2";
import { ThemeWithMarketData } from "@/src/componentsv1/themes/type";
import { ROUTER_PATH } from "@/src/config/router";
import { StockType } from "@/src/utils/stockType";
import { useIsPc } from "@/src/hooks";
import { HORIZONTAL_SCROLL_ATTR } from "@/src/hooks/useSwipeTabs";

const I18n = I18nUtil.createScoped(Translations);

export interface ThemesInPlayProps {
  /** 市场类型 */
  type: "crypto" | "stock";
  /** 主题数据（服务端获取的前3个涨跌幅主题） */
  themes: ThemeWithMarketData[];
  /** 股票类型（仅当 type 为 "stock" 时使用） */
  stockType?: StockType;
  /** 容器额外样式 */
  className?: string;
}

/**
 * 首页投资主题模块
 * - 展示24小时涨跌幅前三的主题
 * - 大屏：一行三个展示全
 * - 小屏：固定卡片宽度，超出横向滚动
 */
export const ThemesInPlay: React.FC<ThemesInPlayProps> = ({
  type,
  themes,
  stockType,
  className,
}) => {
  const isCrypto = type === "crypto";
  const [isPc] = useIsPc();

  // 构建主题详情页链接
  const getHref = (item: ThemeWithMarketData) => {
    const basePath = isCrypto
      ? ROUTER_PATH.MARKET_CRYPTO
      : stockType === "HK"
        ? ROUTER_PATH.RADAR_HK_STOCK
        : ROUTER_PATH.RADAR_US_STOCK;
    return `${basePath}/${item.slug}`;
  };

  // 主题列表页链接
  const themesListHref = isCrypto
    ? ROUTER_PATH.THEMES_CRYPTO
    : stockType === "HK"
      ? ROUTER_PATH.THEMES_HK_STOCK
      : ROUTER_PATH.THEMES_US_STOCK;

  if (!themes || themes.length === 0) {
    return null;
  }

  const displayThemes = isPc ? themes.slice(0, 3) : themes;

  return (
    <div
      className={cn(
        "flex flex-col md:px-0 px-4 ",
        "border-b border-border_white_transparent10 md:border-b-0 pb-4 md:pb-0",
        className,
      )}
    >
      {/* 头部标题 */}
      <SectionHeader title={I18n.t("Trending Themes")} href={themesListHref} />
      {/* 小屏横向滚动，大屏网格布局 */}
      <div
        {...{ [HORIZONTAL_SCROLL_ATTR]: "true" }}
        className="flex overflow-x-auto gap-3 -mx-4 px-4 no-scrollbar md:grid md:grid-cols-3 md:mx-0 md:px-0 md:pb-0 md:overflow-visible"
      >
        {displayThemes.map((item) => (
          <ThemeCardV2
            key={`themes-in-play-${item.slug}`}
            href={getHref(item)}
            showSymbols={!!item.symbols?.length}
            itemData={item}
            className="min-w-[260px] flex-shrink-0 md:min-w-0 md:flex-shrink"
            descClassName="line-clamp-3"
          />
        ))}
      </div>
    </div>
  );
};
